import { ServerAppConfig, AppConfig } from '@client/core/config/types';
import { fromJson } from '@client/core/data/concerns/parses-from-json';
import { make } from '@client/core/data/concerns/parses-from-json/make';
import { AuthUser } from '@client/features/auth/models/auth-user';
import { Project } from '@client/features/project/models/project';
import { Workspace } from '@client/features/workspace/models/workspace';
import { WorkspaceNavigation } from '@client/features/navigation/models/workspace-navigation';

/**
 * Stringify + Parsing makes this object more challenging for an attacker or
 * nosy user to manually overriding application config via something like this
 * in the browser console:
 * `window.AppConfig.workspace.owner_email = 'hacked-email-here@gmail.com'`
 */
const cachedAppConfig = Object.freeze(JSON.parse(JSON.stringify((window as any).AppConfig as AppConfig)));
const cachedConfig = buildAppConfig(cachedAppConfig);

export function buildAppConfig(baseConfig: ServerAppConfig): Readonly<AppConfig> {
  return Object.freeze({
    ...baseConfig,

    currentUser: baseConfig.auth_user
      ? fromJson(AuthUser, baseConfig.auth_user)
      : make(AuthUser, {
          id: 0,
          firstName: '',
          lastName: '',
          email: '',
          avatar: null,
          phone: '',
          title: '',
          isAdmin: false,
          isOwner: false,
          isTeamAdmin: false,
          isTeamMember: false,
          isImpersonated: false,
          featureAccess: {},
          featuresAllowed: [],
          company: null,
          subscription: null,
          amountPaid: 0,
          siteKey: '',
          firstPaymentAt: null,
          lastPaymentAt: null,
          totalNumLogins: 0,
          lastLoginAt: null,
          workspaceOwnerId: null,
        }),

    currentProject: baseConfig.current_project ? fromJson(Project, baseConfig.current_project) : null,

    loggedIn: Boolean(baseConfig.auth_user),

    workspace: baseConfig.workspace
      ? fromJson(Workspace, baseConfig.workspace)
      : new Workspace(0, '', 0, '', null, false, false, 0),

    workspaceNavigation: fromJson(WorkspaceNavigation, baseConfig.workspaceNavigation),
  });
}

export function getAppConfig(): Readonly<AppConfig> {
  return cachedConfig;
}
